import {
  Box,
  Button,
  ButtonGroup,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import React from 'react'

import Chip from '@/components/Chip'

type FrameworkEntity = {
  internallayer_advice: any[]
  frameworkIndex: number
  SetFrameworkIndex: React.Dispatch<React.SetStateAction<number>>
}

const Framework: React.FC<FrameworkEntity> = ({
  frameworkIndex,
  SetFrameworkIndex,
  internallayer_advice = [],
}) => {
  const theme = useTheme()
  const selectedFramework = internallayer_advice[frameworkIndex] || {}

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      sx={{
        borderRadius: '8px',
        background: '#292929',
      }}
    >
      {/* Button Group */}
      <Box
        sx={{
          borderTopLeftRadius: '8px',
          background: 'linear-gradient(98deg, #232323 0%, #1A1A1A 100%)',
        }}
      >
        <ButtonGroup
          sx={{
            border: 'none',
            borderColor: theme.palette.action.disabled,
            borderWidth: '2px',
            '& .MuiButtonGroup-grouped': {
              borderColor: theme.palette.action.disabled,
              borderWidth: '1px',
            },
          }}
          variant="contained"
        >
          {internallayer_advice.length > 0 &&
            internallayer_advice.map((_, i) => (
              <Button
                key={i}
                sx={{
                  borderRadius: '8px 8px 0px 0px',
                  backgroundColor:
                    frameworkIndex === i
                      ? theme.palette.background.disableTab
                      : '#303030',
                  boxShadow: 'none',
                  border: 'none',
                  fontFamily: 'Inter',
                  padding: '6px 16px',
                  textTransform: 'none',
                  borderTopLeftRadius: i === 0 ? '8px' : 0,
                  color:
                    frameworkIndex === i
                      ? theme.palette.primary.main
                      : theme.palette.action.disabled,
                  '&.MuiButtonGroup-grouped': {
                    borderRight:
                      i !== internallayer_advice.length - 1
                        ? `1px solid #4F4F4F`
                        : 'none',
                  },
                }}
                onClick={() => SetFrameworkIndex(i)}
              >
                Option {i + 1}
              </Button>
            ))}
        </ButtonGroup>
      </Box>

      {/* Framework Details */}
      <Box
        mt={0}
        px={4}
        pt={4}
        pb={3}
        bgcolor={'#292929'}
        display="inline-block"
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography fontSize={'16px'} fontWeight={400} lineHeight={'20px'}>
            Framework:
          </Typography>
          {selectedFramework.capbrand && (
            <Chip
              label={selectedFramework.capbrand}
              sx={{
                bgcolor: theme.palette.background.chip,
                color: theme.palette.common.black,
                '&:hover': {
                  bgcolor: theme.palette.background.chip,
                },
              }}
              size="small"
            />
          )}
          {selectedFramework.substructurename && (
            <Chip
              label={selectedFramework.substructurename}
              sx={{
                bgcolor: theme.palette.background.chip,
                color: theme.palette.common.black,
                '&:hover': {
                  bgcolor: theme.palette.background.chip,
                },
              }}
              size="small"
            />
          )}
        </Stack>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          px: 4,
          pb: 4,
          // mx: 'auto',
          background: '#292929',
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ fontWeight: 500, fontSize: '14px', lineHeight: '24px' }}
              >
                Head
              </TableCell>
              <TableCell
                sx={{ fontWeight: 500, fontSize: '14px', lineHeight: '24px' }}
              >
                Cervical
              </TableCell>
              <TableCell
                sx={{ fontWeight: 500, fontSize: '14px', lineHeight: '24px' }}
              >
                Middle
              </TableCell>
              <TableCell
                sx={{ fontWeight: 500, fontSize: '14px', lineHeight: '24px' }}
              >
                Incisal
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Thickness Row */}
            <TableRow>
              <TableCell
                sx={{ fontWeight: 500, fontSize: '14px', lineHeight: '24px' }}
              >
                Thickness
              </TableCell>
              {[
                'thickness_cervical',
                'thickness_middle',
                'thickness_incisal',
              ].map((key, i) => (
                <TableCell key={i}>
                  {selectedFramework[key] !== undefined &&
                  selectedFramework[key] !== null
                    ? `${selectedFramework[key]?.toFixed(2)} mm`
                    : '-'}
                </TableCell>
              ))}
            </TableRow>

            {/* Staining Rows */}
            {selectedFramework.staining ? (
              <>
                {/* Estimated Required Staining */}
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '24px',
                    }}
                  >
                    Estimated Required Staining
                  </TableCell>
                  {[
                    'stainingdelLAB_cervical',
                    'stainingdelLAB_middle',
                    'stainingdelLAB_incisal',
                  ].map((key, i) => (
                    <TableCell key={i}>
                      {selectedFramework[key]?.[1] !== undefined && (
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          <span style={{ color: theme.palette.error.main }}>
                            &#8593;
                          </span>{' '}
                          &Delta;A {selectedFramework[key][1]?.toFixed(2)}
                        </Typography>
                      )}
                      {selectedFramework[key]?.[2] !== undefined && (
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          <span style={{ color: theme.palette.warning.main }}>
                            &#8593;
                          </span>{' '}
                          &Delta;B {selectedFramework[key][2]?.toFixed(2)}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Staining Brand */}
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '24px',
                    }}
                  >
                    Staining Brand
                  </TableCell>
                  {['stain_cervical', 'stain_middle', 'stain_incisal'].map(
                    (key, i) => (
                      <TableCell key={i}>
                        {selectedFramework[key] || '-'}
                      </TableCell>
                    ),
                  )}
                </TableRow>

                {/* Estimated Required Staining in VITA3D */}
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '24px',
                    }}
                  >
                    Estimated Required Staining in VITA3D
                  </TableCell>
                  {[
                    'VITAafterstaining_cervical',
                    'VITAafterstaining_middle',
                    'VITAafterstaining_incisal',
                  ].map((key, i) => (
                    <TableCell key={i}>
                      {selectedFramework[key] || '-'}
                    </TableCell>
                  ))}
                </TableRow>
              </>
            ) : (
              <TableRow>
                <TableCell
                  sx={{ fontWeight: 500, fontSize: '14px', lineHeight: '24px' }}
                >
                  Estimated Required Staining
                </TableCell>
                <TableCell colSpan={3}>
                  {selectedFramework.instructions ||
                    'There is no need to stain the framework, you can start with layering.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default Framework
